/**
 * Bracelet Details Updater
 * Updates bracelet-specific details when variant changes
 * Uses Shopify's existing variant change system - same pattern as price-per-item.js
 */

(function() {
  'use strict';

  // Wait for Shopify's utilities to be available
  function waitForShopifyUtils(callback) {
    // Check for subscribe function (could be global or on window)
    const hasSubscribe = (typeof subscribe !== 'undefined' && typeof subscribe === 'function') || 
                         (window.subscribe && typeof window.subscribe === 'function');
    // Check for PUB_SUB_EVENTS (could be global or on window)
    const hasPubSub = (typeof PUB_SUB_EVENTS !== 'undefined') || 
                      (window.PUB_SUB_EVENTS);
    
    if (hasSubscribe && hasPubSub) {
      callback();
    } else {
      setTimeout(() => waitForShopifyUtils(callback), 100);
    }
  }

  function initializeBraceletDetails() {
    // Find all product details cards with bracelet data
    const beadsDataScripts = document.querySelectorAll('script[id^="beads-data-"]');
    
    beadsDataScripts.forEach((script) => {
      const sectionId = script.id.replace('beads-data-', '');
      
      // Check if already initialized
      if (script.dataset.initialized === 'true') return;
      script.dataset.initialized = 'true';
      
      try {
        const beadsData = JSON.parse(script.textContent);
        const updater = new BraceletDetailsUpdater(sectionId, beadsData);
        updater.init();
      } catch (e) {
        console.error('Error parsing bracelet data:', e);
      }
    });
  }

  class BraceletDetailsUpdater {
    constructor(sectionId, beadsData) {
      this.sectionId = sectionId;
      this.beadsData = beadsData;
      this.variantChangeUnsubscriber = null;
    }

    init() {
      // Use the exact same pattern as price-per-item.js - subscribe directly
      waitForShopifyUtils(() => {
        try {
          // Get subscribe function (could be global or on window)
          const subscribeFn = (typeof subscribe !== 'undefined' && typeof subscribe === 'function') 
            ? subscribe 
            : (window.subscribe && typeof window.subscribe === 'function' ? window.subscribe : null);
          
          // Get PUB_SUB_EVENTS (could be global or on window)
          const pubSubEvents = (typeof PUB_SUB_EVENTS !== 'undefined') 
            ? PUB_SUB_EVENTS 
            : (window.PUB_SUB_EVENTS || null);
          
          if (subscribeFn && pubSubEvents) {
            this.variantChangeUnsubscriber = subscribeFn(
              pubSubEvents.variantChange,
              (event) => {
                if (!event || !event.data) return;
                
                // Match the exact event structure from product-info.js
                const variant = event.data.variant;
                const sectionId = event.data.sectionId;
                
                // Only update if this is for our section
                if (sectionId && sectionId !== this.sectionId) return;
                
                if (variant) {
                  this.updateDetails(variant);
                }
              }
            );
          }
        } catch (e) {
          console.error('Error subscribing to variant change:', e);
        }
      });

      // Also listen directly to variant selector clicks as backup (this is the reliable method)
      this.setupDirectListeners();

      // Initialize with current variant after a short delay
      setTimeout(() => {
        this.updateFromCurrentSelection();
      }, 500);
    }

    setupDirectListeners() {
      // Get variant selector element
      const variantSelects = document.querySelector(`variant-selects[data-section="${this.sectionId}"]`);
      if (!variantSelects) {
        // Retry after a delay
        setTimeout(() => this.setupDirectListeners(), 500);
        return;
      }

      // Store handler reference for cleanup
      this.directClickHandler = (e) => {
        // Check if clicked element is a variant option
        const variantOption = e.target.closest('input[type="radio"], select, [data-option-value], .variant-picker__option, button[data-value]');
        if (variantOption) {
          // Wait a bit for Shopify to update the variant, then update our display
          setTimeout(() => {
            this.updateFromCurrentSelection();
          }, 300);
        }
      };

      this.directChangeHandler = () => {
        setTimeout(() => {
          this.updateFromCurrentSelection();
        }, 300);
      };

      // Use capture phase to catch events early
      variantSelects.addEventListener('click', this.directClickHandler, true);
      variantSelects.addEventListener('change', this.directChangeHandler, true);
    }


    updateFromCurrentSelection() {
      // Get current selected variant from the page (same way Shopify does it)
      const variantSelects = document.querySelector(`variant-selects[data-section="${this.sectionId}"]`);
      if (!variantSelects) return;

      // Try to get variant from script tag (Shopify's method)
      const selectedVariantScript = variantSelects.querySelector('script[data-selected-variant]');
      if (selectedVariantScript) {
        try {
          const variant = JSON.parse(selectedVariantScript.textContent);
          this.updateDetails(variant);
          return;
        } catch (e) {
          // Continue to fallback method
        }
      }

      // Fallback: Get variant from selected inputs
      const selectedInputs = variantSelects.querySelectorAll('input[type="radio"]:checked, select');
      if (selectedInputs.length > 0) {
        const selectedValues = Array.from(selectedInputs).map(input => {
          if (input.type === 'radio') {
            return input.value;
          } else if (input.tagName === 'SELECT') {
            return input.options[input.selectedIndex]?.value;
          }
          return null;
        }).filter(v => v !== null);

        // Find variant in our data that matches
        const variantData = this.beadsData.variants.find((v) => {
          return selectedValues.some(val => {
            if (v.option1 === val) return true;
            // Handle formatted values like "7 mm - (AAAAA Quality)"
            if (val.includes(v.option1) || v.option1.includes(val)) return true;
            return false;
          });
        });

        if (variantData) {
          this.updateByPosition(variantData.position);
        }
      }
    }

    updateDetails(variant) {
      if (!variant || !this.beadsData) return;

      // Get fresh element references each time (in case DOM changed)
      const beadsCountElement = document.getElementById(`beads-count-${this.sectionId}`);
      const braceletSizeElement = document.getElementById(`bracelet-size-${this.sectionId}`);
      const braceletWeightElement = document.getElementById(`bracelet-weight-${this.sectionId}`);

      // Find variant in our data - handle both GID and numeric ID formats
      const variantId = variant.id || variant.variant_id;
      const variantData = this.beadsData.variants.find(v => {
        const vId = v.id.toString();
        const compareId = variantId.toString();
        // Match exact ID or handle GID format
        return vId === compareId || 
               vId.endsWith(compareId) || 
               compareId.endsWith(vId) ||
               vId.includes(compareId) ||
               compareId.includes(vId);
      });

      if (variantData) {
        const position = variantData.position;
        
        // Update beads count
        if (beadsCountElement && this.beadsData.beadsValues && position >= 0 && position < this.beadsData.beadsValues.length) {
          const beadsCount = this.beadsData.beadsValues[position].trim();
          beadsCountElement.textContent = beadsCount;
        }

        // Update bracelet size
        if (braceletSizeElement && this.beadsData.braceletSizeValues && position >= 0 && position < this.beadsData.braceletSizeValues.length) {
          let sizeValue = this.beadsData.braceletSizeValues[position].trim();
          sizeValue = sizeValue.replace(/\s*cm\s*/i, '').trim();
          if (sizeValue && !sizeValue.toLowerCase().includes('cm')) {
            braceletSizeElement.textContent = sizeValue + ' cm';
          } else {
            braceletSizeElement.textContent = sizeValue;
          }
        }

        // Update weight
        if (braceletWeightElement && this.beadsData.weightValues && position >= 0 && position < this.beadsData.weightValues.length) {
          let weightValue = this.beadsData.weightValues[position].trim();
          weightValue = weightValue.replace(/\s*g\s*/i, '').trim();
          if (weightValue && !weightValue.toLowerCase().includes('g')) {
            braceletWeightElement.textContent = weightValue + ' g';
          } else {
            braceletWeightElement.textContent = weightValue;
          }
        }
      }
    }

    updateByPosition(position) {
      if (position < 0) return;

      // Get fresh element references
      const beadsCountElement = document.getElementById(`beads-count-${this.sectionId}`);
      const braceletSizeElement = document.getElementById(`bracelet-size-${this.sectionId}`);
      const braceletWeightElement = document.getElementById(`bracelet-weight-${this.sectionId}`);

      // Update beads count
      if (beadsCountElement && this.beadsData.beadsValues && position < this.beadsData.beadsValues.length) {
        const beadsCount = this.beadsData.beadsValues[position].trim();
        beadsCountElement.textContent = beadsCount;
      }

      // Update bracelet size
      if (braceletSizeElement && this.beadsData.braceletSizeValues && position < this.beadsData.braceletSizeValues.length) {
        let sizeValue = this.beadsData.braceletSizeValues[position].trim();
        sizeValue = sizeValue.replace(/\s*cm\s*/i, '').trim();
        if (sizeValue && !sizeValue.toLowerCase().includes('cm')) {
          braceletSizeElement.textContent = sizeValue + ' cm';
        } else {
          braceletSizeElement.textContent = sizeValue;
        }
      }

      // Update weight
      if (braceletWeightElement && this.beadsData.weightValues && position < this.beadsData.weightValues.length) {
        let weightValue = this.beadsData.weightValues[position].trim();
        weightValue = weightValue.replace(/\s*g\s*/i, '').trim();
        if (weightValue && !weightValue.toLowerCase().includes('g')) {
          braceletWeightElement.textContent = weightValue + ' g';
        } else {
          braceletWeightElement.textContent = weightValue;
        }
      }
    }

    destroy() {
      if (this.variantChangeUnsubscriber) {
        this.variantChangeUnsubscriber();
      }
      
      // Remove direct listeners
      const variantSelects = document.querySelector(`variant-selects[data-section="${this.sectionId}"]`);
      if (variantSelects) {
        if (this.directClickHandler) {
          variantSelects.removeEventListener('click', this.directClickHandler, true);
        }
        if (this.directChangeHandler) {
          variantSelects.removeEventListener('change', this.directChangeHandler, true);
        }
      }
    }
  }

  // Initialize when DOM is ready - same pattern as Shopify theme files
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeBraceletDetails);
  } else {
    initializeBraceletDetails();
  }

  // Also initialize after Shopify's code loads
  waitForShopifyUtils(() => {
    setTimeout(initializeBraceletDetails, 200);
  });
})();


